import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ModalWrapper, ModalCloseButton } from '../common';
import { authService } from '../../api';

// #17: 백엔드 VERIFY_CODE_TTL=600(10분)과 정합화. 모달 안내 문구 "유효시간 10분" 일치
const TIMER_SECONDS = 600; // 10분

export default function VerificationCodeModal({ email, onClose, onVerified }) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [remaining, setRemaining] = useState(TIMER_SECONDS);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [sendError, setSendError] = useState('');
  const intervalRef = useRef(null);
  const sentRef = useRef(false);

  const startTimer = () => {
    clearInterval(intervalRef.current);
    setRemaining(TIMER_SECONDS);
    intervalRef.current = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // 모달 열릴 때 인증코드 발송 (StrictMode 2회 실행 방지: API는 1회, 타이머는 항상 시작)
  useEffect(() => {
    if (!sentRef.current) {
      sentRef.current = true;
      authService.guestVerify({ email }).catch(() => {
        setSendError(t('reservation.verification_send_failed'));
      });
    }
    startTimer();
    return () => clearInterval(intervalRef.current);
  }, [email]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResend = async () => {
    setResending(true);
    setSendError('');
    setError('');
    setCode('');
    try {
      await authService.guestVerify({ email });
      startTimer();
    } catch {
      setSendError(t('reservation.verification_send_failed'));
    } finally {
      setResending(false);
    }
  };

  const minutes = String(Math.floor(remaining / 60)).padStart(2, '0');
  const seconds = String(remaining % 60).padStart(2, '0');
  const expired = remaining === 0;

  const handleConfirm = async () => {
    if (expired) { setError(t('reservation.verification_expired')); return; }
    if (code.trim().length < 4) { setError(t('reservation.verification_input_required')); return; }

    setLoading(true);
    setError('');
    try {
      const res = await authService.guestConfirm({ email, code: code.trim() });
      clearInterval(intervalRef.current);
      onVerified(res.access_token || res.token);
    } catch (err) {
      setError(err.message || t('reservation.verification_invalid'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrapper onClose={onClose} padding="p-5 sm:p-8" gap="gap-5">
      {/* Close button */}
      <div className="absolute top-4 right-4">
        <ModalCloseButton onClick={onClose} />
      </div>

      {/* Title */}
      <h2 className="text-lg font-bold text-mono-900 text-center">{t('reservation.verification_title')}</h2>

      {/* Email */}
      <p className="text-2sm text-mono-500 text-center -mt-2">{email}</p>

      {/* Description */}
      {sendError ? (
        <p className="text-2sm text-red-500 text-center -mt-2">{sendError}</p>
      ) : (
        <p className="text-2sm text-mono-500 text-center leading-relaxed -mt-2 break-keep">
          {t('reservation.verification_desc')}
        </p>
      )}

      {/* Code input with timer */}
      <div className="space-y-1.5">
        <label className="text-2sm font-medium text-mono-600">{t('reservation.verification_label')}</label>
        <div className="relative">
          <input
            type="text"
            value={code}
            onChange={(e) => { setCode(e.target.value); setError(''); }}
            placeholder={t('reservation.verification_placeholder')}
            className="w-full h-12 px-3 pr-16 bg-mono-50 border border-mono-100 rounded-lg text-sm font-medium text-mono-900 placeholder:text-mono-400 focus:outline-none focus:border-orange-500 transition"
            maxLength={6}
          />
          <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-sm font-medium ${expired ? 'text-red-500' : 'text-mono-500'}`}>
            {minutes}:{seconds}
          </span>
        </div>
        {error && <p className="text-2xs text-red-500">{error}</p>}
      </div>

      {/* Bottom buttons */}
      <div className="flex gap-3">
        <Button level="level3" fullWidth onClick={onClose}>{t('common.cancel')}</Button>
        {expired ? (
          <Button level="level1" fullWidth onClick={handleResend} disabled={resending}>
            {resending ? t('reservation.verification_resending') : t('reservation.verification_resend')}
          </Button>
        ) : (
          <Button level="level1" fullWidth onClick={handleConfirm} disabled={!code.trim() || loading}>
            {loading ? t('reservation.verification_checking') : t('common.confirm')}
          </Button>
        )}
      </div>

      {/* #17 Flow E: 이메일 미수신 가이드 — 메일이 안 올 때 사용자가 막막하지 않도록 */}
      <details className="text-2xs text-mono-500 -mt-1">
        <summary className="cursor-pointer text-mono-600 font-medium hover:text-orange-500 transition select-none">
          {t('reservation.verification_help_title')}
        </summary>
        <ul className="mt-2 space-y-1 pl-4 list-disc">
          <li>{t('reservation.verification_help_spam')}</li>
          <li>{t('reservation.verification_help_resend')}</li>
          <li>{t('reservation.verification_help_contact')}</li>
        </ul>
      </details>
    </ModalWrapper>
  );
}
